import { useMemo } from 'react'

import { matchFilterKey, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { BrandThemeModel, BrandThemeRequestOptions } from '@models/types'

const watchEntityKeys = ['brandThemes']

type BrandThemeFilters = {
  brand_id?: number,
  string?: string,
}

type UseBrandThemesOptions = {
  filters?: BrandThemeFilters,
  performHttpRequests?: boolean,
  requestOptions?: BrandThemeRequestOptions,
}

function useBrandThemes(options: UseBrandThemesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    brand_id: filterBrandId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { brandThemes: brandThemesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { brandThemes } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredBrandThemes = useMemo(() => {
    const filtered = Object.values(brandThemes).filter((brandTheme: BrandThemeModel) => {
      const { brand_id, id, title } = brandTheme

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      // @ts-expect-error TS2345
      const matchBrand = matchFilterKey(brand_id, filterBrandId)

      return matchBrand && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [brandThemesUpdatedAt, JSON.stringify(filters)])

  const filteredBrandThemesCount = filteredBrandThemes.length
  const hasFilteredBrandThemes = !!filteredBrandThemesCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredBrandThemesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const reduxActionPayload = useReduxAction(
    'brandThemes',
    'loadBrandThemes',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  const { loading: loadingBrandThemes, reduxActionPerformed } = reduxActionPayload

  return {
    brandThemes,
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrandThemes,
    filteredBrandThemesCount,
    hasFilteredBrandThemes,
    loading: loadingBrandThemes,
    reduxActionPerformed,
  }
}

export default useBrandThemes
